/**
 * Venue-aware date/time/number formatters. Every event/booking/session
 * datetime rendered in the SPA MUST resolve its "when" in the venue's
 * timezone, not the viewer's device timezone. Same for currency + numbers
 * where non-en-GB locales matter (IDR thousands separator convention).
 *
 * The bug this closes: a Sydney visitor viewing Madruga's site (London
 * venue) would see event times shifted 10h (AEST) rather than London.
 * More acutely for PESO (Asia/Makassar), same-day events could flip to
 * the wrong day for anyone whose phone is on a non-Bali timezone.
 * Diagnosed 2026-09-26 in the Love Inn missing-event audit.
 *
 * Usage: pull `venue.timeZone` + `venue.locale` from the by-host
 * response (added Wave 0.2) and pass them into these helpers. Both
 * accept the raw venue object OR nulls and fall back to platform
 * defaults (Europe/London + en-GB), so callers on a legacy Venue shape
 * still work without a runtime error.
 */

export const DEFAULT_TIME_ZONE = "Europe/London";
export const DEFAULT_LOCALE = "en-GB";

type VenueLike = {
  timeZone?: string | null;
  locale?: string | null;
} | null | undefined;

const tzValidCache = new Map<string, boolean>();
function isValidTimeZone(tz: string): boolean {
  const cached = tzValidCache.get(tz);
  if (cached !== undefined) return cached;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
    tzValidCache.set(tz, true);
    return true;
  } catch {
    tzValidCache.set(tz, false);
    return false;
  }
}

/**
 * The venue's IANA timezone with fallback to Europe/London on missing or
 * invalid values. Invalid values are logged once per string via the
 * validity cache so bad DB rows are noisy in dev but never crash a render.
 */
export function venueTimeZone(venue: VenueLike): string {
  const raw = venue?.timeZone?.trim();
  if (!raw) return DEFAULT_TIME_ZONE;
  if (isValidTimeZone(raw)) return raw;
  // eslint-disable-next-line no-console
  console.warn(`[venue-format] invalid venue timeZone ${JSON.stringify(raw)}, falling back to ${DEFAULT_TIME_ZONE}`);
  return DEFAULT_TIME_ZONE;
}

/**
 * The venue's BCP-47 locale with fallback to en-GB. Not validated
 * against a registry (locale-tag misuse just produces slightly odd
 * numbers, never a runtime crash).
 */
export function venueLocale(venue: VenueLike): string {
  const raw = venue?.locale?.trim();
  return raw || DEFAULT_LOCALE;
}

// ── Date / time formatters ────────────────────────────────────────────────

type DateInput = Date | string | number | null | undefined;

function coerceDate(input: DateInput): Date | null {
  if (input == null) return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Weekday + day-of-month + month + time in the venue's timezone.
 * Replacement for the plain `.toLocaleDateString(...)` calls that were
 * device-local across the SPA (~20 sites per 2026-09-26 Love Inn audit).
 *
 * Example: `formatEventDate(event.date, venue)` → "Thu, 10 Sep 20:00"
 * (venue-local, regardless of viewer's timezone).
 */
export function formatEventDate(input: DateInput, venue: VenueLike): string {
  const d = coerceDate(input);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(venueLocale(venue), {
      timeZone: venueTimeZone(venue),
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
}

/**
 * Long-form date (no time). "Saturday, 26 September 2026" in venue TZ.
 */
export function formatEventDateLong(input: DateInput, venue: VenueLike): string {
  const d = coerceDate(input);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(venueLocale(venue), {
      timeZone: venueTimeZone(venue),
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

/**
 * Short time only, "20:00" style. Used for event-detail sub-headline.
 */
export function formatEventTime(input: DateInput, venue: VenueLike): string {
  const d = coerceDate(input);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(venueLocale(venue), {
      timeZone: venueTimeZone(venue),
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
}

/**
 * Short calendar date + weekday, "Sat, 26 Sep" style. Used for event
 * carousel + event list rows.
 */
export function formatEventShortDate(input: DateInput, venue: VenueLike): string {
  const d = coerceDate(input);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(venueLocale(venue), {
      timeZone: venueTimeZone(venue),
      weekday: "short",
      day: "2-digit",
      month: "short",
    }).format(d);
  } catch {
    return "";
  }
}

/**
 * Optional dual-zone renderer. Peso's brief §3 wants the owner dashboard
 * to show WITA (venue) alongside AEST for the Owner in Australia. Callers
 * pass the secondary TZ; falls back to venue-only if omitted.
 */
export function formatEventDateDual(
  input: DateInput,
  venue: VenueLike,
  secondaryTimeZone?: string,
): string {
  const primary = formatEventDate(input, venue);
  if (!secondaryTimeZone || !primary) return primary;
  const d = coerceDate(input);
  if (!d) return primary;
  try {
    const secondary = new Intl.DateTimeFormat(venueLocale(venue), {
      timeZone: secondaryTimeZone,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
    return `${primary} (${secondary})`;
  } catch {
    return primary;
  }
}
